import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { CustomPagination } from 'components/featured/Deals/components/DealsTable/Pagination';
import { SortPopover } from 'components/shared/SortPopOver';

import { ReactComponent as MailIcon } from 'assets/mail-dark.svg';
import { ReactComponent as PhoneIcon } from 'assets/phone-icon.svg';

import RoutePaths from 'routes/RoutePaths';
import zeplinColor from 'theme/Color';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setPage, setPageSize, setSortBy, setSortOrder } from 'store/slices/businessesSlice';
import Logo from '../Logo';
import NoResults from '../NoResults';

export interface Column {
  id: 'name' | 'type' | 'activeDeals' | 'totalSpend' | 'clientFitScore' | 'keyContact' | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
}

export const Columns: readonly Column[] = [
  { id: 'name', label: 'Name' },
  { id: 'type', label: 'Type' },
  { id: 'activeDeals', label: 'Active Deals' },
  { id: 'totalSpend', label: 'Total Spend' },
  { id: 'clientFitScore', label: 'Client Fit Score' },
  { id: 'keyContact', label: 'Key Contact' },
  { id: 'actions', label: '', align: 'right' },
];

const SORTABLE_COLUMNS = ['name', 'activeDeals', 'totalSpend', 'clientFitScore'];

const SORT_LABELS: Record<string, { asc: string; desc: string }> = {
  activeDeals: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
  totalSpend: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
  clientFitScore: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
};

const TableView = () => {
  const [sortAnchor, setSortAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [sortConfig, setSortConfig] = useState<{ field: string; order: 'asc' | 'desc' }>();
  const dispatch = useAppDispatch();

  const {
    businesses,
    loading,
    pagination: { page, pageSize, total, totalPages },
  } = useAppSelector((state) => state.businesses);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      dispatch(setPage(newPage + 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleRowsChange = (newRows: number) => {
    dispatch(setPageSize(newRows));
    dispatch(setPage(1));
  };

  if (loading === 'in-progress') {
    return (
      <Box>
        <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none', marginTop: '24px' }}>
          <TableContainer
            sx={{
              overflow: 'auto',
              height: { xs: 'calc(100vh - 190px)', xl: 'calc(100vh - 450px)' },
            }}
          >
            <Table stickyHeader aria-label='sticky table'>
              <TableHead>
                <TableRow sx={headerRowStyles}>
                  {Columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      <Skeleton variant='text' width={100} height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((index) => (
                  <TableRow key={index}>
                    {Columns.map((column) => (
                      <TableCell key={column.id} align={column.align} sx={{ padding: '22px 12px' }}>
                        <Skeleton variant='text' width='80%' height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  }
  if (!businesses.length) return <NoResults />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflowY: 'auto',
      }}
    >
      <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none', marginTop: '24px' }}>
        <TableContainer
          sx={{
            overflow: 'auto',
            height: 'calc(100vh - 290px)',
          }}
        >
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow sx={headerRowStyles}>
                {Columns.map((column) => {
                  const isSortable = SORTABLE_COLUMNS.includes(column.id);

                  return (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      onClick={(e) =>
                        isSortable && setSortAnchor({ el: e.currentTarget, id: column.id })
                      }
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='body3'>{column.label}</Typography>
                        {isSortable && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                            aria-label='button'
                          >
                            <UnfoldMoreIcon
                              sx={{
                                fontSize: 16,
                                color: zeplinColor.BasicBlack,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {businesses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={Columns.length} align='center'>
                    <Typography variant='body2'>No businesses found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                businesses.map((row, ind) => {
                  const { companyId } = row;

                  return (
                    <TableRow
                      hover
                      key={ind}
                      component={Link}
                      to={`${RoutePaths.Businesses}/${companyId}`}
                      sx={{
                        '&:last-child td, &:last-child th': {
                          borderBottom: `2px solid ${zeplinColor.Neutral5}`,
                        },
                        textDecoration: 'none',
                        color: 'inherit',
                        bgcolor: 'none',
                        borderLeft: `2px solid ${zeplinColor.Neutral5}`,
                        cursor: 'pointer',
                      }}
                    >
                      {Columns.map((column) => {
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            onClick={(e) => {
                              if (column.id === 'actions') {
                                e.stopPropagation();
                              }
                            }}
                            sx={{
                              padding: '22px 12px',
                              borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                              borderTop: `1px solid ${zeplinColor.Neutral5}`,
                            }}
                          >
                            {renderCell(column.id, row)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SortPopover
        anchorEl={sortAnchor?.el || null}
        setAnchorEl={() => setSortAnchor(null)}
        sortOption={sortConfig && sortConfig.field === sortAnchor?.id ? sortConfig.order : 'asc'}
        onSortChange={(order) => {
          if (sortAnchor) {
            setSortConfig({ field: sortAnchor.id, order });
            dispatch(setSortOrder(order));
            dispatch(setSortBy(sortAnchor.id));
            dispatch(setPage(0));
          }
          setSortAnchor(null);
        }}
        label={Columns.find((c) => c.id === sortAnchor?.id)?.label || ''}
        ascLabel={sortAnchor ? SORT_LABELS[sortAnchor.id]?.asc : ''}
        descLabel={sortAnchor ? SORT_LABELS[sortAnchor.id]?.desc : ''}
      />

      <CustomPagination
        currentPage={page - 1}
        totalPages={totalPages}
        rowsPerPage={pageSize}
        totalCount={total}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsChange}
      />
    </Box>
  );
};

const headerRowStyles = {
  '& th': { backgroundColor: '#f5f5f5', borderBottom: 'none' },
  '& th:first-of-type': { borderRadius: '14px 0 0 14px' },
  '& th:last-of-type': { borderRadius: '0 14px 14px 0' },
};

const renderCell = (columnId: string, row: any) => {
  const {
    name,
    logo,
    businessType,
    categories,
    activeDealCount,
    totalSpend,
    clientFitScore,
    keyContact,
  } = row;

  const renders: Record<string, () => JSX.Element | string> = {
    name: () => (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: '240px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '8px',
            height: '8px',
            borderRadius: '100%',
            flexShrink: 0,
          }}
        />
        <Box sx={{ width: '40px', height: '40px' }}>
          <Logo logo={logo} />
        </Box>
        <Typography noWrap sx={{ textOverflow: 'ellipsis' }}>
          {name}
        </Typography>
      </Box>
    ),
    type: () => (
      <Chip
        label={<Typography variant='label1'>{businessType ?? 'N/A'}</Typography>}
        sx={{
          bgcolor: zeplinColor.BasicBlack,
          color: 'white',
          height: '22px',
          fontSize: '12px',
        }}
      />
    ),
    category: () => {
      return (
        <Chip
          label={
            <Typography variant='label1' sx={{ color: zeplinColor.BasicBlack }}>
              {categories[0] ?? 'N/A'}
            </Typography>
          }
          sx={{
            height: '22px',
          }}
        />
      );
    },
    activeDeals: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='body2'>{activeDealCount}</Typography>
      </Box>
    ),
    totalSpend: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='body2'>{totalSpend ?? 'N/A'}</Typography>
      </Box>
    ),
    clientFitScore: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='body2'>{clientFitScore ?? 'N/A'}</Typography>
      </Box>
    ),
    keyContact: () => {
      if (!keyContact) return 'N/A';
      return (
        <Typography variant='body2'>
          {keyContact.name} ({keyContact.title})
        </Typography>
      );
    },

    actions: () => {
      if (keyContact === null || (!keyContact.phone && !keyContact.email)) return 'N/A';
      return (
        <Box
          sx={{
            display: 'inline-flex',
            gap: '4px',
          }}
        >
          {keyContact.phone && (
            <Box
              sx={{
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ':hover': {
                  backgroundColor: zeplinColor.Neutral5,
                  cursor: 'pointer',
                },
              }}
            >
              <a href={`tel:${keyContact.phone}`}>
                <PhoneIcon />
              </a>
            </Box>
          )}

          {keyContact.email && (
            <Box
              sx={{
                borderRadius: '50%',
                alignItems: 'center',
                justifyContent: 'center',
                ':hover': {
                  backgroundColor: zeplinColor.Neutral5,
                  cursor: 'pointer',
                },
              }}
            >
              <a href={`mailto:${keyContact.email}`}>
                <MailIcon />
              </a>
            </Box>
          )}
        </Box>
      );
    },
  };

  return renders[columnId] ? renders[columnId]() : (row as any)[columnId];
};

export default TableView;
